// Corporate-email domain onboarding RPC facades (CYBORG onboarding UI phase).
// Thin async wrappers over the client.* methods (client.ts) for the two surfaces
// that consume them: the joiner flow (TeamFoundCard "Request to join") and the
// admin Members panel (claim domain, join policy, approve/reject requests).
//
// Same shape as ./invitations.ts — pure client facades plus reactive mutation of
// the shared workspaceState proxy (reactive from a plain .ts, so no runes here).
// app.svelte.ts re-exports every symbol so importers hit the app barrel.
import { client } from "./client.js";
import { workspaceState } from "../core/state.svelte.js";
import type {
  JoinPolicy,
  WorkspaceEmailDomainView,
  WorkspaceJoinRequestView,
} from "../core/client.js";

export type { JoinPolicy, WorkspaceEmailDomainView, WorkspaceJoinRequestView };

// ─── Joiner flow ──────────────────────────────────────────────────

// A brand-new signup asks to join the team that owns their email domain. The
// target is passed explicitly (the caller is NOT yet a member, so the RPC uses
// `targetWorkspaceId`, bypassing the relay's membership floor — see client.ts).
export async function requestWorkspaceJoin(
  targetWorkspaceId: string,
): Promise<WorkspaceJoinRequestView> {
  const { request } = await client.requestWorkspaceJoin(targetWorkspaceId);
  return request;
}

// ─── Admin: claimed domain ────────────────────────────────────────

// Admin/owner: the domains this workspace has claimed (0 or 1 — the model is
// exclusive). Defaults to the current workspace.
export async function listWorkspaceDomains(
  workspaceId?: string,
): Promise<WorkspaceEmailDomainView[]> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const { domains } = await client.listWorkspaceDomains(wsId);
  return domains;
}

// Admin/owner: claim `domain` for this workspace (proof = the caller's own email
// is at `domain`; rejected server-side for a public domain or one already owned
// by another workspace). Optional join policy (defaults to "request").
export async function claimWorkspaceDomain(
  domain: string,
  joinPolicy?: JoinPolicy,
  workspaceId?: string,
): Promise<WorkspaceEmailDomainView> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const res = await client.claimWorkspaceDomain(wsId, domain, joinPolicy);
  return res.domain;
}

// Admin/owner: release a claimed domain (idempotent).
export async function unclaimWorkspaceDomain(domain: string, workspaceId?: string): Promise<void> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  await client.unclaimWorkspaceDomain(wsId, domain);
}

// Admin/owner: change how @domain signups join (invite_only | request | open).
export async function setWorkspaceJoinPolicy(
  domain: string,
  joinPolicy: JoinPolicy,
  workspaceId?: string,
): Promise<WorkspaceEmailDomainView> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const res = await client.setWorkspaceJoinPolicy(wsId, domain, joinPolicy);
  return res.domain;
}

// ─── Admin: join requests inbox ───────────────────────────────────

// Admin/owner: the join requests for this workspace. The approvals list passes
// "pending" to show only actionable rows.
export async function listJoinRequests(
  status?: "pending" | "approved" | "rejected",
  workspaceId?: string,
): Promise<WorkspaceJoinRequestView[]> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const { requests } = await client.listJoinRequests(wsId, status);
  return requests;
}

// Admin/owner: approve a pending request (adds the requester as a member). The
// approved requester joins the roster server-side; the caller refreshes members
// where it matters (the Members page re-lists on the live push).
export async function approveJoinRequest(
  requestId: string,
  role?: "admin" | "member" | "viewer",
  workspaceId?: string,
): Promise<WorkspaceJoinRequestView> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const { request } = await client.approveJoinRequest(wsId, requestId, role);
  return request;
}

// Admin/owner: reject a pending request (grants nothing).
export async function rejectJoinRequest(
  requestId: string,
  workspaceId?: string,
): Promise<WorkspaceJoinRequestView> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const { request } = await client.rejectJoinRequest(wsId, requestId);
  return request;
}
